/**
 * SignatureSection — Зөвшөөрлийн хуудасны §4 «ХАРИУЦЛАГАТАЙ ИТА».
 *
 * HRT хуучин системийн AT_Step4Licenser / Admin / Performer / Client / HAB
 * гэсэн ТАВАН хүснэгтийн орлуулга. Тэнд тав тусдаа дэлгэц, тусдаа контроллер
 * байсныг энд НЭГ компонентоор гүйцээв — дүр бүр ижил бүтэцтэй.
 *
 * Юуг шийдэж байна вэ:
 *   Өмнө нь манай системд наряд дээр ЗӨВХӨН 2 хүн (наряд өгөгч + ахлагч)
 *   бичигддэг байсан. Гэтэл хэвлэдэг маягтын §4-т ТАВАН дүр, тус бүр 4
 *   хүртэл хүн гарын үсэг зурдаг. Тэр дутуугаар хэвлэсэн хуудас хүчингүй.
 *
 * Дотоод ба гадны хүн:
 *   • дотоод — Б/Д (ТН) оруулахад нэр/албан тушаал лавлахаас автоматаар
 *   • гадны (гэрээт байгууллага) — регистр + нэрийг ГАРААР
 *   HRT-д ч яг ийм байсан (`s4_ccStore`).
 *
 * Backend: /xab/hr-signatures (GET · POST · PUT · DELETE)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Radio,
  Select,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  getJobSignatures,
  addJobSignature,
  updateJobSignature,
  deleteJobSignature,
} from "src/api/high_risk";
import { xabHrRefApi } from "src/api/xabApi";
import { useUserContext } from "src/contexts/userContext";

/** Дүр тус бүрийн өнгө — маягт дээрх дарааллыг нүдээр ялгахад. */
const PARTY_COLOR = {
  client: "#0EA5E9",
  admin: "#7C3AED",
  performer: "#B45309",
  licenser: "#DC2626",
  hab: "#16A34A",
};

/**
 * PersonModal — §4-т хүн НЭМЭХ ба ЗАСАХ.
 *
 * ⚠ Нэг модал хоёр үүрэгтэй. `editing` дамжуулбал засварын горим:
 *   • дүр (party) ба дотоод/гадны төрөл ӨӨРЧЛӨГДӨХГҮЙ. Дүр солих нь
 *     §5/§6-д зурсан гарын үсэгтэй зөрчилдөж, «хэн юуг зөвшөөрсөн»
 *     түүхийг эвдэнэ — өөр хүн болгох бол устгаад дахин нэмнэ.
 *   • дотоод ажилтны Б/Д ч өөрчлөгдөхгүй. Нэр, албан тушаал, утас,
 *     байгууллага л засагдана (backend PUT-ын зөвшөөрдөг талбарууд).
 *
 * ⚠ ГЭРЭЭТ ХҮНИЙГ ЛАВЛАХААС СОНГОНО. Урьд нь овог/нэр/регистрийг наряд
 *   бүрд гараас дахин бичдэг байсан тул нэг хүн «Батбаяр» · «Б.Батбаяр»
 *   гэж олон хэлбэрээр бүртгэгдэж, хэн хэдэн наряд дээр ажилласныг
 *   тоолох боломжгүй байв. Лавлахад байхгүй бол ГАРААР бичих салаа
 *   хэвээр үлдэнэ — лавлах дутуу байгаа нь ажил зогсоох шалтгаан биш.
 */
function PersonModal({ open, party, editing, onClose, onSubmit }) {
  const isEdit = !!editing;
  // "internal" — ТН-ээр лавлахаас, "external" — гэрээт байгууллагын хүн
  const [kind, setKind] = useState("internal");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  // Гэрээт байгууллага + түүний ажилчид
  const [companies, setCompanies] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [refUnavailable, setRefUnavailable] = useState(false);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  // Лавлахад байхгүй хүнийг гараар бичих горим
  const [manual, setManual] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setKind(editing.is_external ? "external" : "internal");
      setForm({
        worker_tn: editing.worker_tn || "",
        reg_no: editing.reg_no || "",
        last_name: editing.last_name || "",
        first_name: editing.first_name || "",
        position_name: editing.position_name || "",
        dept_name: editing.dept_name || "",
        company_legacy_id: editing.company_legacy_id ?? undefined,
        company_name: editing.company_name || "",
        phone: editing.phone || "",
      });
      // Засварт лавлахын сонголт биш, бэлэн утгыг нь шууд харуулна
      setManual(true);
    } else {
      setKind("internal");
      setForm({});
      setManual(false);
    }
  }, [open, isEdit, editing]);

  // Гэрээт байгууллагын жагсаалт — ГАДНЫ (is_internal=false) нь л хэрэгтэй
  useEffect(() => {
    if (!open || kind !== "external") return;
    xabHrRefApi
      .all()
      .then((d) => {
        const all = d?.companies || [];
        const ext = all.filter((c) => !c.is_internal);
        // ⚠ `is_internal` тугийг хөтлөөгүй бол ГАДНЫ нь хоосон гарна.
        //   Тэр үед бүх байгууллагыг үзүүлэх нь хоосон жагсаалтаас дээр —
        //   хэрэглэгч сонгох юмгүй тэнэхээс сэргийлнэ.
        setCompanies(ext.length ? ext : all);
      })
      .catch(() => setCompanies([]));
  }, [open, kind]);

  // Байгууллага сонгоход түүний ажилчид
  const companyId = form.company_legacy_id;
  useEffect(() => {
    if (!open || kind !== "external" || companyId == null) {
      setWorkers([]);
      return;
    }
    let alive = true;
    setLoadingWorkers(true);
    xabHrRefApi
      .companyWorkers(companyId)
      .then((d) => {
        if (!alive) return;
        setWorkers(d?.items || []);
        setRefUnavailable(!!d?.unavailable);
      })
      .catch(() => {
        if (!alive) return;
        setWorkers([]);
        setRefUnavailable(false);
      })
      .finally(() => {
        if (alive) setLoadingWorkers(false);
      });
    return () => {
      alive = false;
    };
  }, [open, kind, companyId]);

  const set = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  /** Лавлахын ажилтан сонгоход талбаруудыг дүүргэнэ. */
  const pickWorker = (id) => {
    const w = workers.find((x) => x.id === id);
    if (!w) return;
    setForm((p) => ({
      ...p,
      company_worker_id: w.id,
      reg_no: w.reg_no || "",
      last_name: w.last_name || "",
      first_name: w.first_name || "",
      position_name: w.position_name || "",
      phone: w.phone || p.phone || "",
    }));
  };

  const companyName = useMemo(
    () => companies.find((c) => c.legacy_id === companyId)?.name || "",
    [companies, companyId],
  );

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        // Backend PUT нь ЯГ эдгээр талбарыг л зөвшөөрнө
        await onSubmit({
          id: editing.id,
          last_name: form.last_name,
          first_name: form.first_name,
          position_name: form.position_name,
          dept_name: form.dept_name,
          company_name: form.company_name,
          phone: form.phone,
        });
      } else {
        await onSubmit(
          kind === "internal"
            ? { worker_tn: form.worker_tn, phone: form.phone }
            : {
                reg_no: form.reg_no,
                last_name: form.last_name,
                first_name: form.first_name,
                position_name: form.position_name,
                company_legacy_id: form.company_legacy_id,
                company_name: companyName || form.company_name,
                company_worker_id: form.company_worker_id,
                phone: form.phone,
              },
        );
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const hasName =
    !!String(form.last_name || "").trim() ||
    !!String(form.first_name || "").trim();
  const canSubmit = isEdit
    ? hasName
    : kind === "internal"
      ? !!String(form.worker_tn || "").trim()
      : hasName;

  const label = (t) => (
    <div style={{ fontSize: 12, color: "#8A94A6", marginTop: 6 }}>{t}</div>
  );

  // Нэр/албан тушаалын талбарууд хэзээ гарах вэ
  const showNameFields =
    isEdit || (kind === "external" && (manual || !!form.company_worker_id));

  return (
    <Modal
      open={open}
      title={
        isEdit ? `${party?.label} — засах` : `${party?.label} — хүн нэмэх`
      }
      onCancel={onClose}
      onOk={submit}
      okText={isEdit ? "Хадгалах" : "Нэмэх"}
      cancelText="Болих"
      confirmLoading={busy}
      okButtonProps={{ disabled: !canSubmit }}
      width={560}
      destroyOnClose
    >
      {/* Засварт төрөл солигдохгүй — дээрх тайлбарыг үз */}
      {!isEdit ? (
        <Radio.Group
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          style={{ marginBottom: 12 }}
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="internal">Үйлдвэрийн ажилтан</Radio.Button>
          <Radio.Button value="external">Гэрээт байгууллага</Radio.Button>
        </Radio.Group>
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12, fontSize: 12 }}
          message={
            editing.is_external
              ? "Гэрээт байгууллагын хүн"
              : `Үйлдвэрийн ажилтан · Б/Д ${editing.worker_tn || "—"}`
          }
          description="Дүр болон хүнийг өөрчлөх боломжгүй — өөр хүн болгох бол устгаад дахин нэмнэ үү. Нэр, албан тушаал, утас засагдана."
        />
      )}

      {kind === "internal" && !isEdit ? (
        <>
          {label("Б/Д (табелийн дугаар)")}
          <Input
            autoFocus
            value={form.worker_tn || ""}
            onChange={(e) => set("worker_tn")(e.target.value.replace(/\D/g, ""))}
            onPressEnter={() => canSubmit && submit()}
            placeholder="Жишээ: 92540"
          />
          <div style={{ fontSize: 12, color: "#8A94A6", marginTop: 6 }}>
            Овог, нэр, албан тушаал, цех нь ажилтны лавлахаас автоматаар
            татагдана.
          </div>
        </>
      ) : null}

      {kind === "external" && !isEdit ? (
        <>
          {label("Гэрээт байгууллага")}
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Байгууллагаа сонгоно уу"
            value={form.company_legacy_id}
            optionFilterProp="label"
            onChange={(v) =>
              setForm((p) => ({
                ...p,
                company_legacy_id: v,
                // ⚠ Байгууллага солигдвол өмнөх ажилтны утга ҮЛДЭХ ЁСГҮЙ —
                //   өөр байгууллагын хүн бичигдэх аюултай
                company_worker_id: undefined,
                reg_no: "",
                last_name: "",
                first_name: "",
                position_name: "",
              }))
            }
            options={companies.map((c) => ({
              value: c.legacy_id,
              label: c.register_no ? `${c.name} — ${c.register_no}` : c.name,
            }))}
          />

          {companyId != null ? (
            <>
              {label("Ажилтан")}
              {refUnavailable ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ fontSize: 12 }}
                  message="Ажилтны лавлах бэлэн болоогүй"
                  description={
                    <span>
                      <code>create_hr_company_worker.sql</code> миграцыг
                      ажиллуулна уу. Одоохондоо гараар бичнэ.
                    </span>
                  }
                />
              ) : (
                <Select
                  showSearch
                  allowClear
                  loading={loadingWorkers}
                  style={{ width: "100%" }}
                  placeholder={
                    workers.length
                      ? "Ажилтнаа сонгоно уу"
                      : "Энэ байгууллагад ажилтан бүртгэгдээгүй"
                  }
                  value={form.company_worker_id}
                  optionFilterProp="label"
                  onChange={(v) => {
                    if (v == null) {
                      setForm((p) => ({ ...p, company_worker_id: undefined }));
                      return;
                    }
                    pickWorker(v);
                    setManual(false);
                  }}
                  options={workers.map((w) => ({
                    value: w.id,
                    label: w.position_name
                      ? `${w.full_name} — ${w.position_name}`
                      : w.full_name,
                  }))}
                />
              )}
              <div style={{ marginTop: 6 }}>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => {
                    setManual(true);
                    setForm((p) => ({
                      ...p,
                      company_worker_id: undefined,
                      reg_no: "",
                      last_name: "",
                      first_name: "",
                      position_name: "",
                    }));
                  }}
                >
                  <i className="fas fa-plus" style={{ marginRight: 4 }} />
                  Жагсаалтад алга — гараар бичих
                </Button>
                <span style={{ fontSize: 11, color: "#A8AFBC", marginLeft: 8 }}>
                  Байнга ажилладаг хүнийг лавлахад бүртгэвэл дараа нь сонгоод
                  л болно.
                </span>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {showNameFields ? (
        <>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#8A94A6" }}>Овог</div>
              <Input value={form.last_name || ""} onChange={set("last_name")} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#8A94A6" }}>Нэр</div>
              <Input
                value={form.first_name || ""}
                onChange={set("first_name")}
              />
            </div>
          </div>
          {kind === "external" && !isEdit ? (
            <>
              {label("Регистрийн дугаар")}
              <Input value={form.reg_no || ""} onChange={set("reg_no")} />
            </>
          ) : null}
          {isEdit && editing.is_external ? (
            <>
              {label("Байгууллага")}
              <Input
                value={form.company_name || ""}
                onChange={set("company_name")}
              />
            </>
          ) : null}
          {label("Албан тушаал")}
          <Input
            value={form.position_name || ""}
            onChange={set("position_name")}
          />
        </>
      ) : null}

      {label("Утасны дугаар")}
      <Input value={form.phone || ""} onChange={set("phone")} />
    </Modal>
  );
}

export default function SignatureSection({ jobId, readonly }) {
  const { user } = useUserContext();
  const myTn = String(user?.tn || "");

  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [adding, setAdding] = useState(null);   // party объект — нэмэх
  const [editing, setEditing] = useState(null); // { party, item } — засах
  // ⚠ Backend-ээс ирсэн ДҮРИЙН ЗӨРЧИЛ. Нэг хүн «Ажилд оруулагч» ба
  //   «Ажил гүйцэтгэгч» хоёуланд нь орвол хараат бус хяналт алга болно —
  //   өөрийгөө шалгаж, өөртөө зөвшөөрөл өгч байгаа хэрэг. Мөр нэмэгдсэн
  //   ч ЭНЭ нь чимээгүй өнгөрч болохгүй.
  const [conflicts, setConflicts] = useState([]);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await getJobSignatures(jobId);
      setParties(res?.parties || []);
      setUnavailable(!!res?.unavailable);
    } catch (ex) {
      setParties([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const wrap = async (fn, okText) => {
    try {
      const res = await fn();
      // Амжилттай хариунд ирсэн дүрийн зөрчлийг ил гаргана
      const cf = res?.conflicts;
      if (Array.isArray(cf) && cf.length) setConflicts(cf);
      if (okText) message.success(okText);
      load();
      return res;
    } catch (ex) {
      message.error(ex?.response?.data?.message || "Үйлдэл амжилтгүй.");
      throw ex;
    }
  };

  // ⚠ Наряд хараахан ХАДГАЛАГДААГҮЙ бол `jobId` байхгүй — гарын үсэг нь
  //   `job_id`-д холбогддог тул эхлээд нарядаа хадгалах шаардлагатай.
  if (!jobId) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-md p-2 mb-2">
        <div className="font-semibold text-slate-700 mb-1">
          <i className="fas fa-user-shield mr-1" />
          Хариуцлагатай ИТА (§4)
        </div>
        <div style={{ fontSize: 12, color: "#8A94A6" }}>
          Нарядаа эхлээд хадгалсны дараа хариуцлагатай ИТА-г бүртгэнэ.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-2 mb-2">
      <div className="font-semibold text-slate-700 mb-2 flex items-center justify-between">
        <span>
          <i className="fas fa-user-shield mr-1" />
          Хариуцлагатай ИТА (зөвшөөрлийн хуудасны §4)
        </span>
        {loading ? (
          <span style={{ fontSize: 12, color: "#8A94A6" }}>ачааллаж…</span>
        ) : null}
      </div>

      {conflicts.length ? (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setConflicts([])}
          style={{ marginBottom: 8 }}
          message={<b>⚠ Дүрийн зөрчил — хараат бус хяналт алдагдаж байна</b>}
          description={
            <div style={{ fontSize: 12 }}>
              {conflicts.map((c) => (
                <div key={c.party} style={{ marginTop: 2 }}>
                  Энэ хүн <b>«{c.label}»</b> дүрд аль хэдийн бүртгэгдсэн
                  байна. {c.reason}
                </div>
              ))}
              <div style={{ marginTop: 4, color: "#92400E" }}>
                Бүртгэл хийгдсэн — гэхдээ өөр хүн томилох боломжтой эсэхийг
                нягтална уу.
              </div>
            </div>
          }
        />
      ) : null}

      {unavailable ? (
        <div
          style={{
            fontSize: 12,
            color: "#92400E",
            background: "#FEF3C7",
            padding: "6px 8px",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          Хүснэгт бэлэн болоогүй байна — <code>create_hr_job_signature.sql</code>{" "}
          миграцыг ажиллуулна уу.
        </div>
      ) : null}

      {parties.map((p) => (
        <div key={p.party} style={{ marginBottom: 10 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              borderLeft: `3px solid ${PARTY_COLOR[p.party] || "#94A3B8"}`,
              paddingLeft: 8,
              marginBottom: 4,
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13 }}>{p.label}</span>
            <span style={{ fontSize: 11, color: "#8A94A6" }}>
              {p.items.length}/{p.max}
            </span>
            {!readonly && p.items.length < p.max ? (
              <Button size="small" type="link" onClick={() => setAdding(p)}>
                <i className="fas fa-plus" style={{ marginRight: 4 }} />
                Нэмэх
              </Button>
            ) : null}
          </div>

          {p.items.length === 0 ? (
            <div
              style={{ fontSize: 12, color: "#B0B7C3", paddingLeft: 11 }}
            >
              — бүртгэгдээгүй
            </div>
          ) : (
            <table style={{ width: "100%", fontSize: 12 }}>
              <tbody>
                {p.items.map((it) => (
                  <tr key={it.id} style={{ borderTop: "1px solid #EDEFF3" }}>
                    <td style={{ padding: "4px 6px", width: 80 }}>
                      <Tag color={it.is_external ? "orange" : "blue"}>
                        {it.bd_rd || "—"}
                      </Tag>
                    </td>
                    <td style={{ padding: "4px 6px", fontWeight: 600 }}>
                      {it.full_name || "—"}
                      {it.is_external && it.company_name ? (
                        <span style={{ color: "#8A94A6", fontWeight: 400 }}>
                          {" "}
                          · {it.company_name}
                        </span>
                      ) : null}
                    </td>
                    <td style={{ padding: "4px 6px", color: "#8A94A6" }}>
                      {it.dept_name || ""}
                    </td>
                    <td style={{ padding: "4px 6px", color: "#8A94A6" }}>
                      {it.position_name || ""}
                    </td>
                    <td style={{ padding: "4px 6px", color: "#8A94A6" }}>
                      {it.phone || ""}
                    </td>
                    <td style={{ padding: "4px 6px", width: 130 }}>
                      {/* ХАБ-Н ЗААВАРЧИЛГАА ӨГСӨН БАЙДАЛ — маягтын баруун
                          талын багана (HRT: HABCheck) */}
                      <Tooltip title="ХАБ-ын зааварчилгаа өгсөн">
                        <Checkbox
                          disabled={readonly}
                          checked={!!it.hab_check}
                          onChange={(e) =>
                            wrap(
                              () =>
                                updateJobSignature({
                                  id: it.id,
                                  hab_check: e.target.checked,
                                }),
                              null,
                            )
                          }
                        >
                          <span style={{ fontSize: 11 }}>ХАБ заавар</span>
                        </Checkbox>
                      </Tooltip>
                    </td>
                    <td style={{ padding: "4px 6px", width: 118 }}>
                      {it.signed_date ? (
                        <Tag color="green" style={{ fontSize: 10 }}>
                          <i className="fas fa-check mr-1" />
                          {it.signed_date}
                        </Tag>
                      ) : String(it.worker_tn || "") === myTn && !readonly ? (
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          onClick={() =>
                            wrap(
                              () =>
                                updateJobSignature({
                                  id: it.id,
                                  signed: true,
                                }),
                              "Гарын үсэг зурлаа.",
                            )
                          }
                        >
                          Гарын үсэг
                        </Button>
                      ) : (
                        <span style={{ color: "#C6CBD4", fontSize: 11 }}>
                          зураагүй
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "4px 6px", width: 30 }}>
                      {!readonly ? (
                        <Tooltip title="Засах">
                          <Button
                            size="small"
                            type="text"
                            onClick={() => setEditing({ party: p, item: it })}
                          >
                            <i className="fas fa-pen" style={{ fontSize: 11 }} />
                          </Button>
                        </Tooltip>
                      ) : null}
                    </td>
                    <td style={{ padding: "4px 6px", width: 30 }}>
                      {!readonly ? (
                        <Button
                          size="small"
                          type="text"
                          danger
                          onClick={() =>
                            Modal.confirm({
                              title: "Устгах уу?",
                              content: it.full_name,
                              okText: "Устгах",
                              cancelText: "Болих",
                              okButtonProps: { danger: true },
                              onOk: () =>
                                wrap(
                                  () => deleteJobSignature(it.id),
                                  "Устгалаа.",
                                ),
                            })
                          }
                        >
                          <i className="fas fa-times" />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      {/* Нэмэх */}
      <PersonModal
        open={!!adding}
        party={adding}
        onClose={() => setAdding(null)}
        onSubmit={(payload) =>
          wrap(
            () =>
              addJobSignature({
                job_id: jobId,
                party: adding.party,
                ...payload,
              }),
            "Нэмэгдлээ.",
          )
        }
      />

      {/* Засах — ижил модал, `editing` дамжуулснаар горим солигдоно */}
      <PersonModal
        open={!!editing}
        party={editing?.party}
        editing={editing?.item}
        onClose={() => setEditing(null)}
        onSubmit={(payload) => wrap(() => updateJobSignature(payload), "Хадгаллаа.")}
      />
    </div>
  );
}
